import { Link, StatusAborted, StatusOK, StatusRunning } from "@backstage/core-components";
import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import { Text } from "@backstage/ui";
import {
	AVD_DESKTOP_MINE_PATH,
	AVD_DESKTOP_MINE_POWER_PATH,
	AVD_DESKTOP_UNKNOWN,
	AVD_PLUGIN_ID,
	type AvdDesktopStatus,
	type AvdDesktopView,
	type AvdPowerState,
	type BundleType,
	isHoldActive,
	isInFlight,
	type AvdDesktopRecord,
	type MyAvdDesktopResponse,
	type MyAvdPowerResponse,
} from "@internal/backstage-plugin-avd-common";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode, useEffect, useState } from "react";

import type { BundleStatusMark } from "../bundle/bundleDescriptors";
import { type BundleActionState, BundleCard, BundleFact, GatedAction } from "../bundle/bundlePage";

import { MY_AVD_REMOVE_PATH } from "./avdEntitlement";
import {
	AVD_RESOURCE_GROUP,
	avdSubscriptionName,
	desktopIacVersion,
	iacVersionSummary,
} from "./desktopFacts";

// The live half of My AVD: the registry read, the desktop card and the action gating. `unknown` is the
// permanent fail-open state, so the card claims nothing and all three actions stay live (docs/app/avd-registry.md § How the page reads and gates on the registry).

// Re-poll cadence while a lifecycle run is still moving the desktop. Matches the 15s the backend
// reconciler throttles on (avd-common/desktop.ts). Terminal states do not schedule a re-poll.
const POLL_INTERVAL_MS = 15_000;

// Re-poll cadence for the LIVE power state while a `ready` desktop is on screen. Slower than the
// lifecycle poll above — power changes on human timescales (you click connect, it starts) and the read
// is a live ARM call the backend caches for ~30s, so polling faster would only serve the same cache.
const POWER_POLL_INTERVAL_MS = 30_000;

// AVD web client — signing in shows the desktops assigned to your account. It is itself stateful:
// if you already have a desktop it appears here; if not, the list is empty → set one up.
const AVD_WEB_CLIENT = "https://client.wvd.microsoft.com/arm/webclient/";

const useStyles = makeStyles(() => ({
	mono: { fontFamily: "var(--bui-font-monospace)", wordBreak: "break-all" },
	alert: { color: "var(--bui-fg-danger)" },
}));

type MyAvdDesktopState = {
	view: AvdDesktopView;
	runUrl?: string;
	// The fields the "Your AVD" info row needs. Present only for a known status. The subscription NAME
	// is stored on per-colleague rows; for older rows it is derived from `slug`.
	vmName?: string;
	slug?: string;
	subscriptionName?: string;
	// Desired vs actual IaC version (ADR-0020): the row's bundle and the version it was applied from.
	bundleType?: BundleType;
	iacVersion?: string;
	// Set while the desktop is on the inactivity clock: the reclaim date its warning named (#969).
	deleteOn?: string;
	// The row's own clock and hold, so a page that changes the hold can work out deleteOn again.
	lifecycle?: AvdDesktopRecord["lifecycle"];
	noticeDeadline?: string;
	holdUntil?: string;
	loading: boolean;
};

/** The reclaim date to show: only for a live desktop on the clock, and never while a hold covers it. */
export function deleteOnFor(
	row: Pick<AvdDesktopRecord, "lifecycle" | "noticeDeadline" | "holdUntil"> & {
		status: AvdDesktopView;
	},
	now: Date,
): string | undefined {
	if (row.status !== "ready" || row.lifecycle !== "expiring") return undefined;
	return isHoldActive(row.holdUntil, now) ? undefined : row.noticeDeadline;
}

/**
 * Reads the signed-in user's desktop state from the registry, failing open to `unknown` on any error,
 * and re-polls every {@link POLL_INTERVAL_MS} while a run is in flight.
 */
export function useMyAvdDesktop(): MyAvdDesktopState {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	const [state, setState] = useState<MyAvdDesktopState>({
		view: AVD_DESKTOP_UNKNOWN,
		loading: true,
	});

	useEffect(() => {
		let active = true;
		let timer: ReturnType<typeof setTimeout> | undefined;

		const load = async () => {
			try {
				const baseUrl = await discoveryApi.getBaseUrl(AVD_PLUGIN_ID);
				const res = await fetchApi.fetch(`${baseUrl}${AVD_DESKTOP_MINE_PATH}`);
				if (!res.ok) throw new Error(`registry responded ${res.status}`);
				const body = (await res.json()) as MyAvdDesktopResponse;
				if (!active) return;
				if (body.status === AVD_DESKTOP_UNKNOWN) {
					// Permanent fail-open: no row, no fields, no re-poll.
					setState({ view: AVD_DESKTOP_UNKNOWN, loading: false });
					return;
				}
				// Surface the fields the "Your AVD" info row needs.
				setState({
					view: body.status,
					runUrl: body.runUrl,
					vmName: body.vmName,
					slug: body.slug,
					subscriptionName: body.subscriptionName,
					bundleType: body.bundleType,
					iacVersion: body.iacVersion,
					deleteOn: deleteOnFor(body, new Date()),
					lifecycle: body.lifecycle,
					noticeDeadline: body.noticeDeadline,
					holdUntil: body.holdUntil,
					loading: false,
				});
				// Keep watching only while a run is still moving the desktop; terminal states settle.
				if (isInFlight(body.status)) {
					timer = setTimeout(load, POLL_INTERVAL_MS);
				}
			} catch {
				// Fail open — see the FAIL OPEN note above. Never throws, never blanks the page.
				if (active) setState({ view: AVD_DESKTOP_UNKNOWN, loading: false });
			}
		};

		void load();
		return () => {
			active = false;
			if (timer) clearTimeout(timer);
		};
	}, [discoveryApi, fetchApi]);

	return state;
}

/**
 * Reads the signed-in user's LIVE power state from the registry's power endpoint, failing open to
 * `unavailable` on any error, and re-polls every {@link POWER_POLL_INTERVAL_MS} while `enabled`.
 *
 * Only fetches when `enabled` (the caller passes `view === "ready"`) — a desktop that isn't ready has no
 * meaningful power line, and there's no reason to make the ARM-backed call for it. When disabled it holds
 * `unavailable`, which renders nothing.
 */
export function useMyAvdPower(enabled: boolean): AvdPowerState {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	const [power, setPower] = useState<AvdPowerState>("unavailable");

	useEffect(() => {
		if (!enabled) {
			setPower("unavailable");
			return undefined;
		}

		let active = true;
		let timer: ReturnType<typeof setTimeout> | undefined;

		const load = async () => {
			try {
				const baseUrl = await discoveryApi.getBaseUrl(AVD_PLUGIN_ID);
				const res = await fetchApi.fetch(`${baseUrl}${AVD_DESKTOP_MINE_POWER_PATH}`);
				if (!res.ok) throw new Error(`power endpoint responded ${res.status}`);
				const body = (await res.json()) as MyAvdPowerResponse;
				if (!active) return;
				setPower(body.power);
			} catch {
				// Fail open — the endpoint itself already degrades to `unavailable`; this covers the
				// transport failing outright. The box renders nothing for `unavailable`.
				if (active) setPower("unavailable");
			}
			// Keep watching so `starting → running` becomes visible without a manual refresh.
			if (active) timer = setTimeout(load, POWER_POLL_INTERVAL_MS);
		};

		void load();
		return () => {
			active = false;
			if (timer) clearTimeout(timer);
		};
	}, [discoveryApi, fetchApi, enabled]);

	return power;
}

/** The status in a sentence for a KNOWN status; a `ready` desktop needs none, its badge says it. Copy is user-facing ("Set up"/"Remove", not "provision"). */
export function statusSentence(view: AvdDesktopStatus, reclaimed?: string): string {
	switch (view) {
		case "ready":
			return "";
		case "provisioning":
			return "Setting up your desktop — this takes about 10–15 minutes. We'll notify you when it's ready.";
		case "deprovisioning":
			return "Removing your desktop…";
		case "failed":
			return "Your last desktop action didn't finish. You can try Set up or Remove again.";
		// The desktop was built; we could not confirm it came online. That is a gap in OUR checking, not
		// evidence of a broken desktop, so this invites the one action that settles it.
		case "unconfirmed":
			return "Your desktop was set up, but we couldn't confirm it finished starting. Try Connect — it may already be waiting for you.";
		case "absent":
			return reclaimed ?? "You don't have a desktop yet — set one up below.";
	}
}

/** The badge on the desktop card; none while loading or `unknown`, and none for an absent desktop. */
export function avdStatusMark(
	view: AvdDesktopView,
	loading: boolean,
): BundleStatusMark | undefined {
	if (loading || view === AVD_DESKTOP_UNKNOWN) return undefined;
	switch (view) {
		case "ready":
			return { label: "Ready", tone: "ok" };
		case "provisioning":
			return { label: "Setting up…", tone: "progress" };
		case "deprovisioning":
			return { label: "Removing…", tone: "progress" };
		case "failed":
			return { label: "Did not finish", tone: "danger" };
		case "unconfirmed":
			return { label: "Not confirmed", tone: "warning" };
		case "absent":
			return undefined;
	}
}

/** The power line for a `ready` desktop; powered off is healthy (neutral), and `unavailable` renders nothing. */
export function powerLine(power: AvdPowerState): ReactNode {
	switch (power) {
		case "running":
			return <StatusOK>Running</StatusOK>;
		case "starting":
			return <StatusRunning>Starting…</StatusRunning>;
		case "stopping":
		case "deallocating":
		case "stopped":
		case "deallocated":
			return <StatusAborted>Powered off — starts when you connect</StatusAborted>;
		case "unavailable":
			return null;
	}
}

/** Which of Connect / Set up / Remove are enabled for a given desktop state, each with a gating reason. */
export type AvdActions = {
	connect: BundleActionState;
	setup: BundleActionState;
	remove: BundleActionState;
};

const ENABLED: BundleActionState = { enabled: true };
// A run is already moving the desktop; firing another lifecycle action mid-run is never safe.
const IN_FLIGHT_REASON = "A desktop action is already in progress. Wait for it to finish.";

/** Which actions are live per state, each gated one with a reason; fails open. Matrix: docs/app/avd-registry.md. */
export function avdActions(view: AvdDesktopView, loading: boolean): AvdActions {
	if (loading || view === AVD_DESKTOP_UNKNOWN) {
		return { connect: ENABLED, setup: ENABLED, remove: ENABLED };
	}
	switch (view) {
		case "ready":
		case "unconfirmed":
			return { connect: ENABLED, setup: ENABLED, remove: ENABLED };
		case "absent":
			return {
				connect: {
					enabled: false,
					reason: "Set up a desktop first — there's nothing to connect to yet.",
				},
				setup: ENABLED,
				remove: { enabled: false, reason: "You don't have a desktop to remove yet." },
			};
		case "failed":
			return {
				connect: {
					enabled: false,
					reason: "Your last action didn't finish — try Set up or Remove first.",
				},
				setup: ENABLED,
				remove: ENABLED,
			};
		case "provisioning":
		case "deprovisioning":
			return {
				connect: { enabled: false, reason: IN_FLIGHT_REASON },
				setup: { enabled: false, reason: IN_FLIGHT_REASON },
				remove: { enabled: false, reason: IN_FLIGHT_REASON },
			};
	}
}

/** The props the page feeds the desktop card: the resolved desktop state and what it gates. */
export type DesktopCardProps = {
	view: AvdDesktopView;
	loading: boolean;
	runUrl?: string;
	vmName?: string;
	slug?: string;
	subscriptionName?: string;
	bundleType?: BundleType;
	iacVersion?: string;
	/** The commit the desired IaC version came from. Admin-only — the page withholds it otherwise. */
	iacCommitUrl?: string;
	/** ISO-8601. The reclaim date the inactivity warning named; absent unless the desktop is expiring. */
	deleteOn?: string;
	actions: AvdActions;
	/** The owner's hold control, shown on a `ready` desktop. */
	hold?: ReactNode;
	/** The absent line when the last desktop was reclaimed for inactivity (`reclaimText`). */
	reclaimed?: string;
};

/** A reclaim date exactly as the warning wrote it (bundle-evaluate.sh `long_date`): UTC, in words. */
export function deleteOnText(iso: string): string {
	const part = (options: Intl.DateTimeFormatOptions) =>
		new Date(iso).toLocaleDateString("en-GB", { ...options, timeZone: "UTC" });
	return `${part({ weekday: "long" })} ${part({ day: "numeric", month: "long", year: "numeric" })}`;
}

/** The hero sentence; claims nothing while loading or `unknown`, and never repeats `statusLine`. */
export function heroSentence(view: AvdDesktopView, loading: boolean, reclaimed?: string): string {
	if (loading || view === AVD_DESKTOP_UNKNOWN)
		return "Your personal Windows desktop in the cloud.";
	switch (view) {
		case "ready":
			return "Your desktop is ready.";
		case "provisioning":
			return "Your desktop is being set up.";
		case "deprovisioning":
			return "Your desktop is being removed.";
		case "failed":
			return "Something went wrong with your desktop.";
		case "unconfirmed":
			return "Your desktop is set up.";
		case "absent":
			return reclaimed
				? "Your desktop was removed for inactivity."
				: "You don't have a desktop.";
	}
}

/** The desktop's one card; detail: docs/app/avd-registry.md § The desktop card. */
export const DesktopCard = (props: DesktopCardProps) => {
	const classes = useStyles();
	const {
		view,
		loading,
		runUrl,
		vmName,
		slug,
		subscriptionName,
		iacCommitUrl,
		deleteOn,
		hold,
		reclaimed,
		actions,
	} = props;

	// Loading and `unknown` claim nothing: no badge, no sentence, both actions live (fail open).
	const known = loading || view === AVD_DESKTOP_UNKNOWN ? undefined : view;

	// The run is the truth for an in-flight, failed or unconfirmed desktop.
	const canShowRun =
		known !== undefined &&
		(known === "failed" || known === "unconfirmed" || isInFlight(known)) &&
		Boolean(runUrl);

	const facts =
		known === "ready" || canShowRun ? (
			<>
				{known === "ready" ? (
					<>
						<BundleFact label="AVD resource name" value={vmName ?? "—"} mono />
						<BundleFact label="Resource group" value={AVD_RESOURCE_GROUP} mono />
						<BundleFact
							label="Subscription"
							value={slug ? avdSubscriptionName(slug, subscriptionName) : "—"}
							mono
						/>
						<BundleFact label="IaC version">
							<Text as="span" variant="body-small">
								<span className={classes.mono}>
									{iacVersionSummary(desktopIacVersion(props))}
								</span>
								{iacCommitUrl ? (
									<>
										{" · "}
										<Link
											to={iacCommitUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											desired commit
										</Link>
									</>
								) : null}
							</Text>
						</BundleFact>
					</>
				) : null}
				{canShowRun ? (
					<BundleFact label="Last run">
						<Text as="span" variant="body-small">
							<Link to={runUrl!} target="_blank" rel="noopener noreferrer">
								View the run
							</Link>
						</Text>
					</BundleFact>
				) : null}
			</>
		) : undefined;

	return (
		<BundleCard
			name="Personal desktop"
			mark={avdStatusMark(view, loading)}
			status={
				known && known !== "ready" ? (
					<Text as="p" variant="body-small">
						{statusSentence(known, reclaimed)}
					</Text>
				) : undefined
			}
			alert={
				deleteOn ? (
					<Text as="p" variant="body-small" className={classes.alert} role="alert">
						This desktop has not been used for a while. It and everything on it will be
						deleted on <strong>{deleteOnText(deleteOn)}</strong>. Nothing is backed up.
						Connect to it once before then to keep it, or place a hold below.
					</Text>
				) : undefined
			}
			primary={
				<GatedAction
					action={actions.connect}
					label="Connect to my desktop"
					href={AVD_WEB_CLIENT}
					variant="primary"
					external
				/>
			}
			facts={facts}
			body={
				known === "ready" ? (
					<Text as="p" variant="body-small" color="secondary">
						If you need IT to help with your AVD, give them the facts below.
					</Text>
				) : undefined
			}
			hold={known === "ready" ? hold : undefined}
			remove={
				<GatedAction
					action={actions.remove}
					label="Remove my desktop"
					href={MY_AVD_REMOVE_PATH}
					danger
				/>
			}
		/>
	);
};
